import {
  AfterContentChecked, Component, ContentChildren, EventEmitter, forwardRef, HostBinding, Input, OnChanges,
  OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren
} from "@angular/core";
import {AbstractControl, FormBuilder, FormControl, Validators} from "@angular/forms";
import {isArray} from "util";
import {AbstractVisbosControl} from "../abstract-visbos-control";
import {Subscription} from "rxjs/Subscription";
import {FORM_ERROR_MESSAGES} from "../../../assets/form-error-messages";

const STANARD_ERRORS = FORM_ERROR_MESSAGES;

@Component({
  selector: 'vb-generic-form-group',
  templateUrl: './generic-form-group.component.html',
  styleUrls: ['./generic-form-group.component.css']
})
export class GenericFormGroupComponent implements OnInit, OnChanges, AfterContentChecked, OnDestroy {

  @HostBinding('class')
  private get classBinding() {
    return 'form-group ' + this.styleClasses;
  };

  @HostBinding('style.display')
  displayStyle: string = "block";

  @ContentChildren(FormControl)
  private controlContents: QueryList<FormControl>;

  @Input()
  inputErrors: { [errorId: string]: any };

  private _errorDefs: { [errorId: string]: string } = STANARD_ERRORS;

  @Input()
  set errorDefs(value: { [errorId: string]: string }) {
    this._errorDefs = Object.assign({}, this._errorDefs, value);
  }

  get errorDefs(): { [errorId: string]: string } {
    return this._errorDefs;
  }

  errorMessage: string = '';

  private sub: Subscription;

  private _control: AbstractControl | AbstractControl[];

  @Input()
  set ctrl(value: AbstractControl | AbstractControl[]) {
    this._control = value;
  }

  @Input()
  set control(value: AbstractControl | AbstractControl[]) {
    this._control = value;
  }

  get control(): AbstractControl | AbstractControl[] {
    return this._control;
  }

  get ctrl(): AbstractControl | AbstractControl[] {
    return this._control;
  }

  @Input()
  label: string = "";

  @Input()
  storeable: boolean = false;

  @Input()
  storeControlEnabled: boolean = false;

  @Output()
  storeStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  inputId: string;

  @Input()
  helpText: string;

  @Input()
  formHorizontal: boolean = true;

  @Input()
  styleClasses = 'form-group-sm';

  @ViewChildren(forwardRef(() => AbstractVisbosControl))
  childForms: QueryList<AbstractVisbosControl<any, any>> = new QueryList<AbstractVisbosControl<any, any>>();

  constructor(protected fb: FormBuilder) {

  }

  ngOnInit() {
  }

  // Handle error check in ever check run, as touched event of form control cannot be observed, an status change event does not fire on touched
  ngAfterContentChecked(): void {
    if (this.ctrl && !this.inputErrors && !isArray(this.ctrl)) {
      this.processErrors((<AbstractControl>this.ctrl).errors);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    let errors: any;

    if (changes.inputErrors) {
      errors = changes.inputErrors.currentValue;
      this.processErrors(errors);
    }

    if (changes.helpText || changes.ctrl) {

      let ctrl: AbstractControl = (this.ctrl instanceof AbstractControl) ? this.ctrl : null;

      if (!ctrl || !this.helpText) {
        return;
      }

      if (ctrl.validator != Validators.required) {
        if (!this.helpText.endsWith('(optional)')) {
          this.helpText += ' (optional)';
        }
      }
    }

  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  reset() {
    // First, clear recursively all child elements
    this.childForms.forEach(child => {
      child.reset();
    });
  }

  setItemOrDefaultValues(item?) {
    this.childForms.forEach(child => {
      child.setItemOrDefaultValues(item);
    });
  }

  processErrors(errors) {
    if (errors) {
      Object.keys(this.errorDefs).some(key => {
        if (errors[key]) {
          this.errorMessage = this.errorDefs[key];
          return true;
        }
      });
    } else {
      this.errorMessage = '';
    }
  }

  setValidationClasses(ctrl: AbstractControl | AbstractControl[]) {
    let cssClasses = {};

    // Control not set
    if (!ctrl) {
      return cssClasses;
    }

    // Single control
    if (ctrl instanceof AbstractControl) {
      cssClasses = {
        'has-success': ctrl.touched && ctrl.valid,
        'has-error': ctrl.touched && !ctrl.valid,
        'has-warning': !ctrl.touched && !ctrl.valid,
      };
    }

    // Array of controls
    if (isArray(ctrl)) {

      let valid: boolean = true;
      let touched: boolean = false;

      for (let c of <AbstractControl[]>ctrl) {
        if (c && c.invalid) {
          valid = false;
          break;
        }
      }

      for (let c of <AbstractControl[]>ctrl) {
        if (c && c.touched) {
          touched = true;
          break;
        }
      }

      cssClasses = {
        'has-success': touched && valid,
        'has-error': touched && !valid,
        'has-warning': !touched && !valid,
      };

    }

    return cssClasses;
  }

  storeStateClicked(state: boolean) {
    this.storeControlEnabled = state;
    this.storeStateChanged.emit(state);
  }

  toId(label: string): string {
    return label + "-input";
  }

  hasError() {
    return this.ctrl && this.ctrl instanceof AbstractControl && this.ctrl.touched && this.errorMessage;
  }

}



// WEBPACK FOOTER //
// ./src/app/core/visbos-core/ui/forms/generic-form-group/generic-form-group.component.ts